import React, { useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components/macro"; //eslint-disable-line
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";
import {
	SectionHeading,
	Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { SectionDescription } from "components/misc/Typography.js";
import { ReactComponent as ChevronDownIcon } from "feather-icons/dist/icons/chevron-down.svg";
import { useTranslation } from "react-i18next";
import { ReactComponent as MyIcon } from "../../images/point.svg";

// Styled Components
const PrimaryBackgroundContainer = tw(
	Container
)`px-8 bg-gray-100 text-gray-900 py-16`; // Changed background to gray-100, and text color to gray-900 for dark text on a light background

const HeadingContainer = tw.div`flex flex-col items-center`;
const MainLi = tw.li`list-none relative font-semibold`;

const Subheading = tw(SubheadingBase)`text-center text-blue-700 mb-4`; // Blue for subheading
const Heading = tw(SectionHeading)`text-gray-900 text-center`; // Dark text for main heading
const Description = tw(
	SectionDescription
)`mx-auto text-center text-gray-600 max-w-2xl `; // Gray for description text for subtle contrast

const FaqsContainer = tw.div`mt-10 sm:mt-16 w-full flex-1 lg:flex justify-between items-start max-w-screen-xl mx-auto gap-8`;
const FaqsColumn = tw.div`w-full lg:max-w-lg lg:mr-12 last:mr-0`;
const Faq = tw.div`select-none cursor-pointer border-b border-gray-300 hover:border-blue-500 transition-colors duration-300 py-6`; // Updated border colors
const Question = tw.div`flex justify-between items-center`;
const QuestionText = tw.div`text-lg font-semibold text-gray-900`; // Darker text for the questions
const QuestionToggleIcon = styled(motion.span)`
	${tw`ml-2 transition duration-300`}
	svg {
		${tw`w-6 h-6`}
	}
`;
const Answer = tw(motion.div)`hidden text-sm font-normal mt-4 text-gray-700`; // Darker text for the answers

// Styling for bullet points in the answers
const BulletList = styled.ul`
	${tw`ml-6 mt-4 pl-4 list-disc`}
	li {
		${tw`mt-2 text-sm text-gray-700`}// Darker bullet point text for better contrast
	}
`;

const BulletDescription = tw.div`font-bold`;

export default ({
	subheading = "Practice Areas",
	heading = "Our Expertise",
	description = "We provide comprehensive services to meet your security needs, ensuring safety, compliance, and operational efficiency.",
}) => {
	const { t } = useTranslation();

	// Content provided using i18n
	const faqs = [
		{
			question: t("practiceAreas.workPlanTitle"), // "Development of Security Unit Work Plans"
			answer: (
				<>
					<BulletDescription>
						{t("practiceAreas.workPlanDescription")}
					</BulletDescription>
					<BulletList>
						<li>{t("practiceAreas.workPlanTitlePoint1")}</li>
						<li>{t("practiceAreas.workPlanTitlePoint2")}</li>
					</BulletList>
				</>
			),
		},
		{
			question: t("practiceAreas.personnelTitle"), // "Personnel Selection and Management"
			answer: t("practiceAreas.personnelDescription"), // Detailed explanation of this task
			answer: (
				<>
					<BulletDescription>
						{t("practiceAreas.personnelDescription")}
					</BulletDescription>
					<BulletList>
						<MainLi>
							{t("practiceAreas.personnelPoint1")}
							<MyIcon
								style={{
									left: "-22px",
									top: "50%",
									position: "absolute",
									transform: "translateY(-50%)",
								}}
								width="15"
								height="15"
							/>
						</MainLi>
						<li>{t("practiceAreas.personnelPoint2")}</li>
						<li>{t("practiceAreas.personnelPoint3")}</li>
						<li>{t("practiceAreas.personnelPoint4")}</li>
					</BulletList>
				</>
			),
		},
		{
			question: t("practiceAreas.serviceInstructionsTitle"), // "Service Instructions and Regulations"
			answer: (
				<>
					<BulletDescription>
						{t("practiceAreas.serviceInstructionsDescription")}
					</BulletDescription>
					<BulletList>
						<li>{t("practiceAreas.serviceInstructionsPoint1")}</li>
					</BulletList>
				</>
			),
		},
		{
			question: t("practiceAreas.implementationSchedulesTitle"), // "Implementation Schedules"
			answer: (
				<>
					<BulletDescription>
						{t("practiceAreas.implementationSchedulesDescription")}
					</BulletDescription>
					<BulletList>
						<li>{t("practiceAreas.implementationSchedulesPoint1")}</li>
					</BulletList>
				</>
			),
		},
		{
			question: t("practiceAreas.comprehensiveSurveyTitle"), // "Comprehensive Survey and Control"
			// answer: t("practiceAreas.comprehensiveSurveyDescription"),
			answer: (
				<>
					<BulletDescription>
						{t("practiceAreas.comprehensiveSurveyDescription")}
					</BulletDescription>
					<BulletList>
						<MainLi>
							{t("practiceAreas.comprehensiveSurveyPoint1")}
							<MyIcon
								style={{
									left: "-22px",
									top: "50%",
									position: "absolute",
									transform: "translateY(-50%)",
								}}
								width="15"
								height="15"
							/>
						</MainLi>
						<li>{t("practiceAreas.comprehensiveSurveyPoint2")}</li>
						<li>{t("practiceAreas.comprehensiveSurveyPoint3")}</li>
						<li>{t("practiceAreas.comprehensiveSurveyPoint4")}</li>
						<li>{t("practiceAreas.comprehensiveSurveyPoint5")}</li>
					</BulletList>
				</>
			),
		},
		{
			question: t("practiceAreas.securityControlTitle"), // "Security Unit Personnel Control"
			answer: (
				<>
					<BulletDescription>
						{t("practiceAreas.securityControlDescription")}
					</BulletDescription>
					<BulletList>
						<li>{t("practiceAreas.securityControlPoint1")}</li>
					</BulletList>
				</>
			),
		},
	];

	const [activeQuestionIndexLeft, setActiveQuestionIndexLeft] = useState(null); // State for left column
	const [activeQuestionIndexRight, setActiveQuestionIndexRight] =
		useState(null); // State for right column

	const toggleQuestionLeft = (questionIndex) => {
		if (activeQuestionIndexLeft === questionIndex)
			setActiveQuestionIndexLeft(null);
		else setActiveQuestionIndexLeft(questionIndex);
	};

	const toggleQuestionRight = (questionIndex) => {
		if (activeQuestionIndexRight === questionIndex)
			setActiveQuestionIndexRight(null);
		else setActiveQuestionIndexRight(questionIndex);
	};

	const faqCol1 = faqs.filter((_, index) => index % 2 === 0); // Left column content
	const faqCol2 = faqs.filter((_, index) => index % 2 !== 0); // Right column content

	return (
		<PrimaryBackgroundContainer>
			<ContentWithPaddingXl>
				<HeadingContainer>
					<Subheading>{t("practiceAreas.subheading")}</Subheading>
					<Heading>{t("practiceAreas.heading")}</Heading>
					<Description>{t("practiceAreas.description")}</Description>
				</HeadingContainer>
				<FaqsContainer>
					<FaqsColumn>
						{faqCol1.map((faq, index) => (
							<Faq key={index} onClick={() => toggleQuestionLeft(index)}>
								<Question>
									<QuestionText>{faq.question}</QuestionText>
									<QuestionToggleIcon
										variants={{
											collapsed: { rotate: 0 },
											open: { rotate: -180 },
										}}
										initial="collapsed"
										animate={
											activeQuestionIndexLeft === index ? "open" : "collapsed"
										}
										transition={{ duration: 0.2 }}
									>
										<ChevronDownIcon />
									</QuestionToggleIcon>
								</Question>
								<Answer
									variants={{
										open: {
											opacity: 1,
											height: "auto",
											marginTop: "16px",
											display: "block",
										},
										collapsed: {
											opacity: 0,
											height: 0,
											marginTop: "0px",
											display: "none",
										},
									}}
									initial="collapsed"
									animate={
										activeQuestionIndexLeft === index ? "open" : "collapsed"
									}
									transition={{ duration: 0.3 }}
								>
									{faq.answer}
								</Answer>
							</Faq>
						))}
					</FaqsColumn>
					<FaqsColumn>
						{faqCol2.map((faq, index) => (
							<Faq key={index} onClick={() => toggleQuestionRight(index)}>
								<Question>
									<QuestionText>{faq.question}</QuestionText>
									<QuestionToggleIcon
										variants={{
											collapsed: { rotate: 0 },
											open: { rotate: -180 },
										}}
										initial="collapsed"
										animate={
											activeQuestionIndexRight === index ? "open" : "collapsed"
										}
										transition={{ duration: 0.2 }}
									>
										<ChevronDownIcon />
									</QuestionToggleIcon>
								</Question>
								<Answer
									variants={{
										open: {
											opacity: 1,
											height: "auto",
											marginTop: "16px",
											display: "block",
										},
										collapsed: {
											opacity: 0,
											height: 0,
											marginTop: "0px",
											display: "none",
										},
									}}
									initial="collapsed"
									animate={
										activeQuestionIndexRight === index ? "open" : "collapsed"
									}
									transition={{ duration: 0.3 }}
								>
									{faq.answer}
								</Answer>
							</Faq>
						))}
					</FaqsColumn>
				</FaqsContainer>
			</ContentWithPaddingXl>
		</PrimaryBackgroundContainer>
	);
};
